/**
 * PO Issues Dashboard — seed data.
 *
 * The five issue types and the shape of each row's "More Information" cell come
 * from the BCM-897 deck (slides 2-5). Rows are dummy for now: this screen has no
 * middleware endpoint yet, so every row below is shaped exactly like a future
 * /fetchPOIssues response so a real endpoint can be dropped into
 * poIssuesAPI.js without touching the components.
 *
 * Row shape:
 *   {
 *     id, poNumber, issue,            // issue = one of ISSUE_TYPES
 *     info: [{ tag, tone, value }],   // "More Information" chips (see TONES)
 *     showProcessFlowLink,            // Delivery Mismatch only
 *     cmo, productCode, productDescription,
 *     poStatus,                       // one of PO_STATUSES
 *     timestamp,                      // ISO-ish "DD-MMM-YYYY HH:mm:ss" parts
 *     resolvedBy,                     // Closed tab only: { name, date, time }
 *   }
 */

// Chip tones — PO-side values read red, master-data-side values read green,
// neutral context (plant, locations) reads grey. Matches the deck's colouring.
export const TONES = {
  po: 'po',
  md: 'md',
  neutral: 'neutral',
};

// The five names the API accepts in alertsList, in its own order. Matching is
// case-insensitive server-side, so these title-case labels are sent as-is.
//
// 'Delivery Mismatch' is deliberately absent. The deck uses that name for what
// the API calls 'Missing process flow config' — one alert, two vocabularies —
// and nothing ever writes a row named 'Delivery mismatch', so offering it as a
// filter returned zero rows every time.
export const ISSUE_TYPES = [
  'Component Mismatch',
  'Product Missing',
  'Plant Mismatch',
  'SLoc Missing',
  'Missing Process Flow Config',
];

// Which alerts get the "Current process flow configurations" link. Whether an
// alert can be RESOLVED is no longer inferred here — /fetchAllPOAlerts returns
// showMarkAsResolved per row and that is the authority.
export const PROCESS_FLOW_ISSUES = ['Missing Process Flow Config'];

export const PO_STATUSES = ['Created', 'Confirmed', 'Delivered', 'Deleted'];

export const CMO_OPTIONS = [
  'Patheon Toronto',
  'Patheon Whitby',
  'Patheon Monza',
  'PCI Ireland',
  'PCI Rockford',
];

export const OPEN_ISSUES = [
  {
    id: 'open-1',
    poNumber: '40005859',
    issue: 'Component Mismatch',
    info: [
      { tag: 'PO', tone: TONES.po, value: '1258474 · DASATINIB (BMS-354825) SIEGFRIED' },
      { tag: 'MD', tone: TONES.md, value: '1258901 · DASATINIB (BMS-354825) SIEGFRIED MICRONISED' },
    ],
    cmo: 'Patheon Toronto',
    productCode: '464855',
    productDescription: 'DASATINIB TAB 100MG BULK DRUM SIEG PATH',
    poStatus: 'Created',
    timestamp: { date: '08-Aug-2026', time: '06:52:18' },
  },
  {
    id: 'open-2',
    poNumber: '40005941',
    issue: 'Product Missing',
    info: [],
    cmo: 'Patheon Whitby',
    productCode: '471310',
    productDescription: 'APIXABAN TAB 10MG BULK DRUM PATH WHIT',
    poStatus: 'Confirmed',
    timestamp: { date: '08-Aug-2026', time: '05:34:41' },
  },
  {
    id: 'open-3',
    poNumber: '40005971',
    issue: 'Plant Mismatch',
    info: [
      { tag: 'PO PLANT', tone: TONES.po, value: '1007' },
      { tag: 'MD PLANT', tone: TONES.md, value: '1718' },
    ],
    cmo: 'Patheon Monza',
    productCode: '471255',
    productDescription: 'APIXABAN TAB 2.5MG BULK DRUM PATH MONZ',
    poStatus: 'Created',
    timestamp: { date: '07-Aug-2026', time: '21:08:57' },
  },
  {
    id: 'open-4',
    poNumber: '40006004',
    issue: 'SLoc Missing',
    info: [
      { tag: 'PLANT', tone: TONES.neutral, value: '1442' },
      { tag: 'PO SLOC', tone: TONES.po, value: '3105' },
      { tag: 'MD SLOC', tone: TONES.md, value: '3100, 3110' },
    ],
    cmo: 'PCI Ireland',
    productCode: '482017',
    productDescription: 'ELIQUIS TAB 5MG PACK BLISTER PCI IRE',
    poStatus: 'Confirmed',
    timestamp: { date: '07-Aug-2026', time: '14:22:03' },
  },
  {
    id: 'open-5',
    poNumber: '40006046',
    issue: 'Delivery Mismatch',
    info: [
      { tag: 'PICKUP LOCATION', tone: TONES.neutral, value: 'US' },
      { tag: 'DELIVERY LOCATION', tone: TONES.po, value: 'EU' },
    ],
    showProcessFlowLink: true,
    cmo: 'PCI Rockford',
    productCode: '490127',
    productDescription: 'SPRYCEL TAB 100MG PACK BOTTLE PCI RKF',
    poStatus: 'Confirmed',
    timestamp: { date: '06-Aug-2026', time: '19:47:12' },
  },
];

export const CLOSED_ISSUES = [
  {
    id: 'closed-1',
    poNumber: '40005712',
    issue: 'Component Mismatch',
    info: [
      { tag: 'PO', tone: TONES.po, value: '1258474 · DASATINIB (BMS-354825) SIEGFRIED' },
      { tag: 'MD', tone: TONES.md, value: '1258474 · DASATINIB (BMS-354825) SIEGFRIED' },
    ],
    cmo: 'Patheon Toronto',
    productCode: '464855',
    productDescription: 'DASATINIB TAB 100MG BULK DRUM SIEG PATH',
    poStatus: 'Confirmed',
    timestamp: { date: '05-Aug-2026', time: '11:26:44' },
    resolvedBy: { name: 'J. Fernandes', date: '05-Aug-2026', time: '14:02:19' },
  },
  {
    id: 'closed-2',
    poNumber: '40005688',
    issue: 'Plant Mismatch',
    info: [
      { tag: 'PO PLANT', tone: TONES.po, value: '1718' },
      { tag: 'MD PLANT', tone: TONES.md, value: '1718' },
    ],
    cmo: 'Patheon Whitby',
    productCode: '471310',
    productDescription: 'APIXABAN TAB 10MG BULK DRUM PATH WHIT',
    poStatus: 'Created',
    timestamp: { date: '04-Aug-2026', time: '16:51:07' },
    resolvedBy: { name: 'R. Kulkarni', date: '05-Aug-2026', time: '09:18:35' },
  },
  {
    id: 'closed-3',
    poNumber: '40005640',
    issue: 'SLoc Missing',
    info: [
      { tag: 'PLANT', tone: TONES.neutral, value: '1442' },
      { tag: 'PO SLOC', tone: TONES.po, value: '3100' },
      { tag: 'MD SLOC', tone: TONES.md, value: '3100, 3110' },
    ],
    cmo: 'PCI Ireland',
    productCode: '482017',
    productDescription: 'ELIQUIS TAB 5MG PACK BLISTER PCI IRE',
    poStatus: 'Confirmed',
    timestamp: { date: '04-Aug-2026', time: '08:33:52' },
    resolvedBy: { name: "M. O'Brien", date: '04-Aug-2026', time: '12:47:10' },
  },
  {
    id: 'closed-4',
    poNumber: '40005601',
    issue: 'Delivery Mismatch',
    info: [
      { tag: 'PICKUP LOCATION', tone: TONES.neutral, value: 'US' },
      { tag: 'DELIVERY LOCATION', tone: TONES.neutral, value: 'US' },
    ],
    showProcessFlowLink: true,
    cmo: 'PCI Rockford',
    productCode: '490127',
    productDescription: 'SPRYCEL TAB 100MG PACK BOTTLE PCI RKF',
    poStatus: 'Confirmed',
    timestamp: { date: '03-Aug-2026', time: '20:14:29' },
    resolvedBy: { name: 'A. Whitfield', date: '04-Aug-2026', time: '07:22:58' },
  },
];

/**
 * "Current Process Flow Configurations" popup contents.
 *
 * Transcribed from the process-flow decision matrix. The column set and row
 * order match that sheet exactly, and a blank means the rule does not constrain
 * that field. There is no endpoint for this reference data yet, so it lives here.
 */
export const PROCESS_FLOW_CONFIG = [
  { cmoCountry: 'US', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'TRUE',  processFlowID: '2', regionCode: 'US_CA', sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'US', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'TRUE',  processFlowID: '4', regionCode: 'US_CA', sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'CA', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'TRUE',  processFlowID: '4', regionCode: 'US_CA', sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'CA', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'TRUE',  processFlowID: '2', regionCode: 'US_CA', sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'US', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'FALSE', processFlowID: '4', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: ''      },
  { cmoCountry: 'CA', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: 'FALSE', processFlowID: '4', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: ''      },
  { cmoCountry: 'IE', confirmationControlKey: '',     deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '3', regionCode: 'EU',    sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'IT', confirmationControlKey: '',     deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '3', regionCode: 'EU',    sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'IE', confirmationControlKey: '',     deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'IT', confirmationControlKey: '',     deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'IE', confirmationControlKey: '',     deliveryWithinEU: 'FALSE', deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: ''      },
  { cmoCountry: 'IT', confirmationControlKey: '',     deliveryWithinEU: 'FALSE', deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: ''      },
  { cmoCountry: 'US', confirmationControlKey: 'ROTC', deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '1', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: 'TRUE'  },
  { cmoCountry: 'CA', confirmationControlKey: 'ROTC', deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '1', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: 'TRUE'  },
  { cmoCountry: 'US', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'CA', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'US_CA', sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'IE', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'IT', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'BE', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'DE', confirmationControlKey: '',     deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '5', regionCode: 'EU',    sponsorQACheckRequired: '',      shipmentRequired: 'FALSE' },
  { cmoCountry: 'US', confirmationControlKey: 'ROTC', deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '1', regionCode: 'US_CA', sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'US', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '2', regionCode: 'US_CA', sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'US', confirmationControlKey: '1',    deliveryWithinEU: '',      deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'US_CA', sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'BE', confirmationControlKey: '1',    deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '3', regionCode: 'EU',    sponsorQACheckRequired: 'FALSE', shipmentRequired: ''      },
  { cmoCountry: 'BE', confirmationControlKey: '1',    deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'DE', confirmationControlKey: '1',    deliveryWithinEU: 'TRUE',  deliveryWithinUSCanada: '',      processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: 'TRUE',  shipmentRequired: ''      },
  { cmoCountry: 'DE', confirmationControlKey: '1',    deliveryWithinEU: 'FALSE', deliveryWithinUSCanada: 'TRUE',  processFlowID: '4', regionCode: 'EU',    sponsorQACheckRequired: 'TRUE',  shipmentRequired: 'TRUE'  },
];
